import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlockRenderer from "@/components/cms/BlockRenderer";
import ShareBar from "@/components/cms/ShareBar";
import { cmsGet } from "@/lib/api";

export default function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined);
  const [share, setShare] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    cmsGet(slug).then(setPage).catch(() => setPage(null));
  }, [slug]);

  useEffect(() => {
    if (page) document.title = page.seo_title || page.title;
  }, [page]);

  // SEO/social meta: set canonical + Open Graph. Use the SpLink go_url (server-side 307)
  // as the canonical share destination for JS-less crawlers / SMS unfurlers.
  useEffect(() => {
    if (!page) return;
    const upsert = (sel, attr, key, val) => {
      if (!val) return;
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute(sel.startsWith("link") ? "href" : "content", val);
    };
    const shareTarget = share?.go_url || share?.short_url;
    upsert('link[rel="canonical"]', "rel", "canonical", shareTarget);
    upsert('meta[property="og:url"]', "property", "og:url", shareTarget);
    upsert('meta[property="og:type"]', "property", "og:type", "article");
    upsert('meta[property="og:title"]', "property", "og:title", page.seo_title || page.title);
    upsert('meta[property="og:description"]', "property", "og:description", page.seo_description || page.excerpt || "");
    upsert('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    if (page.cover_image) upsert('meta[property="og:image"]', "property", "og:image", page.cover_image);
  }, [page, share]);

  if (page === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={26} /></div>;

  if (page === null) return (
    <><Navbar /><main className="pt-40 pb-40 text-center min-h-screen">
      <h1 className="font-heading text-4xl font-bold text-slate-900">Page not found</h1>
      <Link to="/" className="text-brand mt-4 inline-block">Back home</Link>
    </main><Footer /></>
  );

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <>
      <Navbar />
      <main className="pt-28">
        {page.cover_image && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img src={page.cover_image} alt={page.title} className="w-full h-72 md:h-96 object-cover rounded-2xl" />
          </div>
        )}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {page.status !== "published" && (
            <div className="mb-4 inline-block text-xs font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">Draft preview</div>
          )}
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">{page.title}</h1>
          {page.excerpt && <p className="text-xl text-slate-500 mt-4">{page.excerpt}</p>}
          <div className="mt-8">
            <BlockRenderer blocks={page.blocks} />
          </div>
          <div className="mt-12">
            <ShareBar url={pageUrl} title={page.title} shortUrl={page.splink_short_url} onResolved={setShare} />
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
